import type {
  ContextGap,
  GapStatus,
  InterviewResponse,
  Message,
} from '@/types/api'

export type MessagesByGap = Record<string, Message[]>

export function appendMessage(
  prev: MessagesByGap,
  gapId: string,
  message: Message
): MessagesByGap {
  return { ...prev, [gapId]: [...(prev[gapId] ?? []), message] }
}

export function prependMessage(
  prev: MessagesByGap,
  gapId: string,
  message: Message
): MessagesByGap {
  return { ...prev, [gapId]: [message, ...(prev[gapId] ?? [])] }
}

export function removeMessage(
  prev: MessagesByGap,
  gapId: string,
  messageId: string
): MessagesByGap {
  return {
    ...prev,
    [gapId]: (prev[gapId] ?? []).filter((m) => m.id !== messageId),
  }
}

/** Returns `prev` untouched when the key is already absent. */
export function withoutKey<T>(
  prev: Record<string, T>,
  key: string
): Record<string, T> {
  if (!(key in prev)) return prev
  const next = { ...prev }
  delete next[key]
  return next
}

/** Adds `delta` to a per-key counter, dropping keys that reach zero. */
export function adjustCount(
  prev: Record<string, number>,
  key: string,
  delta: number
): Record<string, number> {
  const value = (prev[key] ?? 0) + delta
  return value > 0 ? { ...prev, [key]: value } : withoutKey(prev, key)
}

/** Local overrides win over the fetched status until the next refetch. */
export function effectiveStatuses(
  gaps: ContextGap[],
  overrides: Record<string, GapStatus>
): Record<string, GapStatus> {
  const map: Record<string, GapStatus> = {}
  for (const gap of gaps) map[gap.id] = overrides[gap.id] ?? gap.status
  return map
}

export function firstResolvedGapId(
  updates: InterviewResponse['gapUpdates']
): string | null {
  const hit = Object.entries(updates ?? {}).find(
    ([, status]) => status === 'resolved'
  )
  return hit ? hit[0] : null
}

/**
 * Resolves when `promise` settles or after `ms`, whichever comes first.
 * Never rejects. Requests have no client timeout, so a hung call must not
 * block whatever waits on it forever.
 */
export function waitAtMost(
  promise: Promise<unknown> | undefined,
  ms: number
): Promise<void> {
  if (!promise) return Promise.resolve()
  let timer: ReturnType<typeof setTimeout> | undefined
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms)
  })
  const settled = promise.then(
    () => undefined,
    () => undefined
  )
  return Promise.race([settled, cap]).finally(() => clearTimeout(timer))
}

let optimisticSeq = 0

/** Client-side user bubble shown until the server reply lands. */
export function makeOptimisticMessage(content: string): Message {
  optimisticSeq += 1
  return {
    id: `optimistic-user-${Date.now()}-${optimisticSeq}`,
    sessionId: '',
    role: 'user',
    content,
    createdAt: new Date().toISOString(),
  }
}
